import { useEffect } from 'react';
import { reportError } from '../../shared/observability/error-tracking.js';
import { Alert, Button, Card, CardBody, CardHeader } from '@hvac/ui';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { isApiError, toDisplayMessage } from '../../shared/api/errors.js';

/**
 * Route-level error boundary.
 *
 * Shows a message the user can act on and the request id they can quote. The
 * underlying error is never rendered: it may contain internal detail, and for
 * anything unexpected the reference is what actually helps support.
 */
export function RouteErrorPage({ error, reset }: ErrorComponentProps) {
  const requestId = isApiError(error) ? error.requestId : null;
  // A refusal the server explained (4xx) is not a defect; everything else is.
  const expected = isApiError(error) && error.httpStatus < 500;
  useEffect(() => {
    if (!expected) reportError(error, requestId === null ? {} : { requestId });
  }, [error, expected, requestId]);

  return (
    <main className="flex min-h-dvh items-center justify-center px-4">
      <Card className="w-full max-w-lg">
        <CardHeader title="Something went wrong" />
        <CardBody className="flex flex-col gap-4">
          <Alert tone="danger" {...(requestId === null ? {} : { requestId })}>
            {toDisplayMessage(error)}
          </Alert>
          <Button
            onClick={() => {
              reset();
            }}
          >
            Try again
          </Button>
        </CardBody>
      </Card>
    </main>
  );
}
